import React, { useMemo, useState } from "react";
import Select from "react-select";
import { FaClock, FaMapMarkerAlt, FaTrophy } from "react-icons/fa";
import { MdOutlineSportsSoccer } from "react-icons/md";
import { SkeletonBlock } from "components/admin/loading";
import { EmptyState } from "components/admin/ui";
import {
  getCompactSelectFieldStyles,
  SelectFieldOption,
} from "components/global/select-field-styles";
import { formatCountdown } from "utils/admin";
import {
  DayHeader,
  DayMeta,
  DayTitle,
  FixtureActionAnchor,
  FixtureActionButton,
  FixtureActions,
  FixtureCardGrid,
  FixtureCardList,
  FixtureCardPattern,
  FixtureCardShell,
  FixtureContext,
  FixtureCountBadge,
  FixtureDayCluster,
  FixtureDaySection,
  FixtureFact,
  FixtureFactBody,
  FixtureFactIcon,
  FixtureFooter,
  FixtureKickMeta,
  FixtureKickMetaStrong,
  FixtureMetaPanel,
  FixtureMetaRow,
  FixtureResults,
  FixtureSkeletonFact,
  FixtureSkeletonFacts,
  FixtureSkeletonFooter,
  FixtureSkeletonHeader,
  FixtureSkeletonMatchup,
  FixtureSkeletonShell,
  FixtureSkeletonTeam,
  FixtureStatusDot,
  FixtureStatusPill,
  FixtureStatusTone,
  KickoffDivider,
  KickoffHeadline,
  MatchBadgeInner,
  MatchBadgeLabel,
  MatchBadgeScoreRow,
  MatchBadgeDivider,
  MatchBadgeSlot,
  MatchBadgeSurface,
  MatchBadgeValue,
  MatchupGrid,
  MetaBody,
  MetaDateTitle,
  MetaSubtext,
  MetaTitle,
  ResultSummary,
  ResultSummaryText,
  ShowcaseClearSlot,
  ShowcaseFilterSelect,
  ShowcaseSearchInput,
  ShowcaseStack,
  ShowcaseToolbar,
  ShowcaseToolbarControls,
  ShowcaseToolbarMeta,
  ShowcaseToolbarSummary,
  ShowcaseToolbarTitle,
  TeamLogoSlot,
  TeamName,
  TeamPlateShell,
  TeamSchool,
  TeamTextPlate,
  ToolbarBadge,
  ToolbarBadgeDot,
} from "./styles/fixtures-showcase.styles";
import {
  FixturesShowcaseTextGroup,
  FixturesShowcasePreviewImage,
} from "components/admin/styles/fixtures-showcase.inline.styles";

export { FixtureActionButton, FixtureActionAnchor } from "./styles/fixtures-showcase.styles";

const showcaseFilterSelectStyles = getCompactSelectFieldStyles();
const menuPortalTarget = typeof document !== "undefined" ? document.body : null;

type TeamSide = {
  id?: string | number;
  name?: string;
  image_url?: string;
  school_name?: string;
  school_image_url?: string;
};
export type FixtureCardRecord = {
  id?: string | number;
  sport_name?: string;
  sport_image_url?: string;
  match_type?: string;
  status?: string;
  competition_name?: string;
  venue_name?: string;
  venue_address?: string;
  scheduled_at?: string;
  result_summary?: string;
  home_score?: string;
  away_score?: string;
  live_links?: {
    stream_url?: string;
    stream_label?: string;
    score_url?: string;
    meeting_url?: string;
    platform?: string;
  };
  countdown?: {
    enabled?: boolean;
    seconds_until_start?: number;
    days_until_start?: number;
    active?: boolean;
  };
  home_school_team?: TeamSide;
  away_school_team?: TeamSide;
  home_school_team_name?: string;
  away_school_team_name?: string;
  home_school_team_image_url?: string;
  away_school_team_image_url?: string;
  home_school_image_url?: string;
  away_school_image_url?: string;
};
type FixturesShowcaseProps = {
  fixtures: FixtureCardRecord[];
  isLoading?: boolean;
  emptyTitle: string;
  emptyDescription: string;
  title?: string;
  onEditFixture?: (fixture: FixtureCardRecord) => void;
  editLabel?: string;
  renderFixtureActions?: (fixture: FixtureCardRecord) => React.ReactNode;
};
type FixtureGroup = {
  competitionLabel: string;
  fixtures: FixtureCardRecord[];
};
type FixtureDayGroup = {
  dateKey: string;
  dateLabel: string;
  groups: FixtureGroup[];
};
type FixturePhase = "all" | "live" | "upcoming" | "completed";
const LIVE_STATUSES = new Set([
  "active",
  "in_progress",
  "in progress",
  "inprogress",
  "live",
  "started",
]);
const COMPLETED_STATUSES = new Set([
  "abandoned",
  "cancelled",
  "canceled",
  "completed",
  "final",
  "finished",
  "full_time",
  "full time",
  "ft",
  "postponed",
]);
const CANCELLED_STATUSES = new Set(["abandoned", "cancelled", "canceled", "postponed"]);
const formatMatchDateLabel = (value?: string) => {
  if (!value) {
    return "Date TBC";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "long",
    weekday: "long",
  });
};
const formatMatchTimeLabel = (value?: string) => {
  if (!value) {
    return "Time TBC";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleTimeString("en-ZA", {
    hour: "2-digit",
    minute: "2-digit",
  });
};
const formatShortMatchDate = (value?: string) => {
  if (!value) {
    return "Date TBC";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    weekday: "short",
  });
};
const formatLabel = (value?: string) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
const getCompetitionLabel = (fixture: FixtureCardRecord) =>
  fixture.competition_name || fixture.sport_name || "Fixture Schedule";
const getFixtureTeam = (fixture: FixtureCardRecord, side: "home" | "away"): TeamSide => {
  if (side === "home") {
    return {
      image_url: fixture.home_school_team_image_url || fixture.home_school_team?.image_url,
      name: fixture.home_school_team_name || fixture.home_school_team?.name,
      school_image_url: fixture.home_school_image_url || fixture.home_school_team?.school_image_url,
      school_name: fixture.home_school_team?.school_name,
    };
  }
  return {
    image_url: fixture.away_school_team_image_url || fixture.away_school_team?.image_url,
    name: fixture.away_school_team_name || fixture.away_school_team?.name,
    school_image_url: fixture.away_school_image_url || fixture.away_school_team?.school_image_url,
    school_name: fixture.away_school_team?.school_name,
  };
};
const getNormalizedStatus = (fixture: FixtureCardRecord) =>
  String(fixture.status || "")
    .trim()
    .toLowerCase();
const isFixtureLive = (fixture: FixtureCardRecord) => {
  const normalizedStatus = getNormalizedStatus(fixture);
  return Boolean(fixture.countdown?.active || LIVE_STATUSES.has(normalizedStatus));
};
const isFixtureCompleted = (fixture: FixtureCardRecord) => {
  const normalizedStatus = getNormalizedStatus(fixture);
  return COMPLETED_STATUSES.has(normalizedStatus);
};
const getFixturePhase = (fixture: FixtureCardRecord): FixturePhase => {
  if (isFixtureLive(fixture)) {
    return "live";
  }
  if (isFixtureCompleted(fixture)) {
    return "completed";
  }
  return "upcoming";
};
const getStatusTone = (fixture: FixtureCardRecord): FixtureStatusTone => {
  if (isFixtureLive(fixture)) {
    return "live";
  }
  const normalized = getNormalizedStatus(fixture);
  if (CANCELLED_STATUSES.has(normalized)) {
    return "cancelled";
  }
  if (
    normalized === "completed" ||
    normalized === "final" ||
    normalized === "finished" ||
    normalized === "ft" ||
    normalized === "full_time" ||
    normalized === "full time"
  ) {
    return "completed";
  }
  if (normalized === "confirmed") {
    return "confirmed";
  }
  if (normalized === "proposed" || normalized === "pending" || normalized === "draft") {
    return "proposed";
  }
  if (normalized === "scheduled") {
    return "default";
  }
  return isFixtureCompleted(fixture) ? "completed" : "default";
};
const buildFixtureSearchText = (fixture: FixtureCardRecord) =>
  [
    fixture.sport_name,
    fixture.match_type,
    fixture.competition_name,
    fixture.venue_name,
    fixture.venue_address,
    fixture.result_summary,
    fixture.status,
    formatLabel(fixture.status),
    formatLabel(fixture.match_type),
    fixture.home_school_team_name,
    fixture.away_school_team_name,
    fixture.home_school_team?.name,
    fixture.away_school_team?.name,
    fixture.home_school_team?.school_name,
    fixture.away_school_team?.school_name,
    getFixturePhase(fixture),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
const fixtureMatchesSearch = (fixture: FixtureCardRecord, searchQuery: string) => {
  const tokens = searchQuery
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) {
    return true;
  }
  const haystack = buildFixtureSearchText(fixture);
  return tokens.every((token) => haystack.includes(token));
};
const formatFixtureCountLabel = (count: number) => `${count} fixture${count === 1 ? "" : "s"}`;
const flattenDayFixtures = (day: FixtureDayGroup) =>
  day.groups.flatMap((group) => group.fixtures);
const findSelectOption = (options: SelectFieldOption[], value: string) =>
  options.find((option) => option.value === value) || options[0] || null;
const ShowcaseFilterDropdown: React.FC<{
  "aria-label": string;
  inputId: string;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  value: string;
}> = ({ "aria-label": ariaLabel, inputId, onChange, options, value }) => (
  <ShowcaseFilterSelect>
    <Select
      aria-label={ariaLabel}
      classNamePrefix="fixture-filter-select"
      inputId={inputId}
      isClearable={false}
      isSearchable={false}
      menuPlacement="auto"
      menuPortalTarget={menuPortalTarget}
      onChange={(option) => onChange(option?.value || "all")}
      options={options}
      styles={showcaseFilterSelectStyles}
      value={findSelectOption(options, value)}
    />
  </ShowcaseFilterSelect>
);

const FixtureCardSkeleton: React.FC = () => (
  <FixtureSkeletonShell>
    <FixtureSkeletonHeader>
      <div className="flex min-w-0 flex-col gap-2">
        <SkeletonBlock className="h-6 w-44 rounded-lg" />
        <SkeletonBlock className="h-3 w-28 rounded-full" />
      </div>
      <SkeletonBlock className="h-7 w-24 shrink-0 rounded-full" />
    </FixtureSkeletonHeader>

    <FixtureSkeletonMatchup>
      <FixtureSkeletonTeam>
        <SkeletonBlock className="h-14 w-14 rounded-[16px]" />
        <SkeletonBlock className="h-4 w-28 rounded-md" />
        <SkeletonBlock className="h-3 w-24 rounded-full" />
      </FixtureSkeletonTeam>
      <SkeletonBlock className="h-16 w-16 rounded-[0.95rem]" />
      <FixtureSkeletonTeam $align="right">
        <SkeletonBlock className="h-14 w-14 rounded-[16px]" />
        <SkeletonBlock className="h-4 w-28 rounded-md" />
        <SkeletonBlock className="h-3 w-24 rounded-full" />
      </FixtureSkeletonTeam>
    </FixtureSkeletonMatchup>

    <FixtureSkeletonFacts>
      <FixtureSkeletonFact>
        <SkeletonBlock className="h-4 w-4 shrink-0 rounded-md" />
        <SkeletonBlock className="h-3.5 w-40 rounded-full" />
      </FixtureSkeletonFact>
      <FixtureSkeletonFact>
        <SkeletonBlock className="h-4 w-4 shrink-0 rounded-md" />
        <SkeletonBlock className="h-3.5 w-28 rounded-full" />
      </FixtureSkeletonFact>
      <FixtureSkeletonFact>
        <SkeletonBlock className="h-4 w-4 shrink-0 rounded-md" />
        <SkeletonBlock className="h-3.5 w-36 rounded-full" />
      </FixtureSkeletonFact>
    </FixtureSkeletonFacts>

    <FixtureSkeletonFooter>
      <SkeletonBlock className="h-4 w-20 rounded-full" />
      <SkeletonBlock className="h-4 w-24 rounded-full" />
      <SkeletonBlock className="h-9 w-28 rounded-[0.9rem]" />
    </FixtureSkeletonFooter>
  </FixtureSkeletonShell>
);

const FixturesShowcaseSkeleton: React.FC<{
  cardCount?: number;
  title: string;
}> = ({ cardCount = 4, title }) => (
  <ShowcaseStack aria-busy="true" aria-label={`Loading ${title}`} role="status">
    <ShowcaseToolbar>
      <ShowcaseToolbarMeta>
        <ShowcaseToolbarTitle>{title}</ShowcaseToolbarTitle>
        <ShowcaseToolbarSummary>
          <SkeletonBlock className="h-3 w-24 rounded-full" />
          <SkeletonBlock className="h-3 w-16 rounded-full" />
          <SkeletonBlock className="h-3 w-20 rounded-full" />
        </ShowcaseToolbarSummary>
      </ShowcaseToolbarMeta>

      <ShowcaseToolbarControls>
        <SkeletonBlock className="h-[2.65rem] w-full rounded-[0.95rem]" />
        <SkeletonBlock className="h-[2.65rem] w-full rounded-[0.95rem]" />
        <SkeletonBlock className="h-[2.65rem] w-full rounded-[0.95rem]" />
        <SkeletonBlock className="h-[2.65rem] w-full rounded-[0.95rem]" />
        <ShowcaseClearSlot>
          <SkeletonBlock className="h-9 w-20 rounded-[0.9rem]" />
        </ShowcaseClearSlot>
      </ShowcaseToolbarControls>
    </ShowcaseToolbar>

    <FixtureResults>
      <FixtureCardList>
        {Array.from({ length: cardCount }).map((_, index) => (
          <FixtureCardSkeleton key={index} />
        ))}
      </FixtureCardList>
    </FixtureResults>
  </ShowcaseStack>
);

const TeamPlate: React.FC<{
  align?: "left" | "right";
  team: TeamSide;
}> = ({ align = "left", team }) => (
  <TeamPlateShell $align={align}>
    <TeamLogoSlot>
      <FixturesShowcasePreviewImage
        alt={team.name || "Team crest"}
        fallbackLabel={team.name}
        src={team.image_url || team.school_image_url}
        variant="team"
      />
    </TeamLogoSlot>

    <TeamTextPlate $align={align}>
      <TeamName>{team.name || "Team TBD"}</TeamName>
      <TeamSchool>{team.school_name || "School pending"}</TeamSchool>
    </TeamTextPlate>
  </TeamPlateShell>
);
const MatchBadge: React.FC<{
  awayScore?: string;
  hasScore: boolean;
  homeScore?: string;
  live?: boolean;
}> = ({ awayScore, hasScore, homeScore, live = false }) => (
  <MatchBadgeSlot>
    <MatchBadgeSurface $hasScore={hasScore} $live={live}>
      {hasScore ? (
        <MatchBadgeInner>
          <MatchBadgeLabel>{live ? "Live" : "Score"}</MatchBadgeLabel>
          <MatchBadgeScoreRow>
            <MatchBadgeValue $hasScore $side="home" title={`Home ${homeScore || "-"}`}>
              {homeScore || "-"}
            </MatchBadgeValue>
            <MatchBadgeDivider aria-hidden>–</MatchBadgeDivider>
            <MatchBadgeValue $hasScore $side="away" title={`Away ${awayScore || "-"}`}>
              {awayScore || "-"}
            </MatchBadgeValue>
          </MatchBadgeScoreRow>
        </MatchBadgeInner>
      ) : (
        <MatchBadgeInner>
          <MatchBadgeLabel>Fixture</MatchBadgeLabel>
          <MatchBadgeValue $side="center">VS</MatchBadgeValue>
        </MatchBadgeInner>
      )}
    </MatchBadgeSurface>
  </MatchBadgeSlot>
);
const FixtureCard: React.FC<{
  editLabel: string;
  fixture: FixtureCardRecord;
  onEditFixture?: (fixture: FixtureCardRecord) => void;
  renderFixtureActions?: (fixture: FixtureCardRecord) => React.ReactNode;
}> = ({ editLabel, fixture, onEditFixture, renderFixtureActions }) => {
  const homeTeam = getFixtureTeam(fixture, "home");
  const awayTeam = getFixtureTeam(fixture, "away");
  const hasScore = Boolean(fixture.home_score || fixture.away_score);
  const live = isFixtureLive(fixture);
  const statusTone = getStatusTone(fixture);
  const scheduledTimeLabel = formatMatchTimeLabel(fixture.scheduled_at);
  const scheduledDateLabel = formatShortMatchDate(fixture.scheduled_at);
  const matchTypeLabel = fixture.match_type ? formatLabel(fixture.match_type) : null;
  const countdownLabel =
    !live && (fixture.countdown?.enabled || fixture.countdown?.seconds_until_start !== undefined)
      ? formatCountdown(fixture.countdown?.seconds_until_start)
      : null;
  const liveLinks = [
    fixture.live_links?.stream_url
      ? {
          href: fixture.live_links.stream_url,
          label: fixture.live_links.stream_label || fixture.live_links.platform || "Watch live",
        }
      : null,
    fixture.live_links?.score_url
      ? {
          href: fixture.live_links.score_url,
          label: "Live score",
        }
      : null,
    fixture.live_links?.meeting_url
      ? {
          href: fixture.live_links.meeting_url,
          label: "Open link",
        }
      : null,
  ].filter(Boolean) as Array<{
    href: string;
    label: string;
  }>;
  const statusLabel = live ? "Live" : formatLabel(fixture.status || "Scheduled");
  const kickMetaParts = [matchTypeLabel, countdownLabel].filter(Boolean);
  const venueLabel = fixture.venue_name || fixture.venue_address;
  return (
    <FixtureCardShell $live={live}>
      <FixtureCardPattern $live={live} />

      <FixtureCardGrid>
        <FixtureMetaPanel>
          <FixtureMetaRow>
            <FixturesShowcaseTextGroup>
              <KickoffHeadline>
                <MetaDateTitle>{scheduledDateLabel}</MetaDateTitle>
                <KickoffDivider aria-hidden>·</KickoffDivider>
                <MetaTitle>{scheduledTimeLabel}</MetaTitle>
              </KickoffHeadline>
            </FixturesShowcaseTextGroup>

            <FixtureStatusPill $tone={statusTone}>
              {live ? <FixtureStatusDot $live /> : null}
              {statusLabel}
            </FixtureStatusPill>
          </FixtureMetaRow>

          {kickMetaParts.length ? (
            <FixtureKickMeta>
              <FaClock aria-hidden size={11} style={{ marginRight: "0.35rem", opacity: 0.8 }} />
              {kickMetaParts.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                  {index > 0 ? " · " : null}
                  {part === countdownLabel ? (
                    <FixtureKickMetaStrong>{part}</FixtureKickMetaStrong>
                  ) : (
                    part
                  )}
                </React.Fragment>
              ))}
            </FixtureKickMeta>
          ) : null}
        </FixtureMetaPanel>

        <MatchupGrid>
          <TeamPlate team={homeTeam} />
          <MatchBadge
            awayScore={fixture.away_score}
            hasScore={hasScore}
            homeScore={fixture.home_score}
            live={live}
          />
          <TeamPlate align="right" team={awayTeam} />
        </MatchupGrid>

        <FixtureContext>
          {fixture.competition_name ? (
            <FixtureFact>
              <FixtureFactIcon aria-hidden>
                <FaTrophy size={13} />
              </FixtureFactIcon>
              <FixtureFactBody>
                <MetaBody>{fixture.competition_name}</MetaBody>
              </FixtureFactBody>
            </FixtureFact>
          ) : null}

          {fixture.sport_name ? (
            <FixtureFact>
              <FixtureFactIcon aria-hidden>
                <MdOutlineSportsSoccer size={15} />
              </FixtureFactIcon>
              <FixtureFactBody>
                <MetaBody>{formatLabel(fixture.sport_name)}</MetaBody>
              </FixtureFactBody>
            </FixtureFact>
          ) : null}

          {venueLabel ? (
            <FixtureFact>
              <FixtureFactIcon aria-hidden>
                <FaMapMarkerAlt size={13} />
              </FixtureFactIcon>
              <FixtureFactBody>
                {fixture.venue_name ? <MetaBody>{fixture.venue_name}</MetaBody> : null}
                {fixture.venue_address ? <MetaSubtext>{fixture.venue_address}</MetaSubtext> : null}
              </FixtureFactBody>
            </FixtureFact>
          ) : null}
        </FixtureContext>
      </FixtureCardGrid>

      {fixture.result_summary ? (
        <ResultSummary>
          <ResultSummaryText>{fixture.result_summary}</ResultSummaryText>
        </ResultSummary>
      ) : null}

      <FixtureFooter>
        <FixtureActions>
          {onEditFixture && renderFixtureActions ? (
            <FixtureActionButton
              $variant="link"
              onClick={() => onEditFixture(fixture)}
              type="button"
            >
              {editLabel}
            </FixtureActionButton>
          ) : null}

          {liveLinks.map((link) => (
            <FixtureActionAnchor $variant="link" href={link.href} key={link.href} rel="noreferrer" target="_blank">
              {link.label}
            </FixtureActionAnchor>
          ))}

          {renderFixtureActions ? renderFixtureActions(fixture) : null}

          {onEditFixture && !renderFixtureActions ? (
            <FixtureActionButton
              $variant="primary"
              onClick={() => onEditFixture(fixture)}
              type="button"
            >
              {editLabel}
            </FixtureActionButton>
          ) : null}
        </FixtureActions>
      </FixtureFooter>
    </FixtureCardShell>
  );
};
export const groupFixturesForShowcase = (fixtures: FixtureCardRecord[]): FixtureDayGroup[] => {
  const dateMap = new Map<string, Map<string, FixtureCardRecord[]>>();
  fixtures.forEach((fixture) => {
    const dateKey = fixture.scheduled_at ? fixture.scheduled_at.slice(0, 10) : "date-tbc";
    const competitionLabel = getCompetitionLabel(fixture);
    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, new Map());
    }
    const competitionMap = dateMap.get(dateKey)!;
    if (!competitionMap.has(competitionLabel)) {
      competitionMap.set(competitionLabel, []);
    }
    competitionMap.get(competitionLabel)!.push(fixture);
  });
  return Array.from(dateMap.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([dateKey, competitionMap]) => ({
      dateKey,
      dateLabel: formatMatchDateLabel(dateKey === "date-tbc" ? undefined : `${dateKey}T00:00:00`),
      groups: Array.from(competitionMap.entries()).map(
        ([competitionLabel, competitionFixtures]) => ({
          competitionLabel,
          fixtures: [...competitionFixtures].sort((left, right) =>
            String(left.scheduled_at || "").localeCompare(String(right.scheduled_at || "")),
          ),
        }),
      ),
    }));
};
export const FixturesShowcase: React.FC<FixturesShowcaseProps> = ({
  fixtures,
  isLoading = false,
  emptyTitle,
  emptyDescription,
  title = "Fixture Browser",
  onEditFixture,
  editLabel = "Open fixture",
  renderFixtureActions,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [phaseFilter, setPhaseFilter] = useState<FixturePhase>("all");
  const [sportFilter, setSportFilter] = useState("all");
  const [competitionFilter, setCompetitionFilter] = useState("all");
  const sportOptions = useMemo(
    () => Array.from(new Set(fixtures.map((fixture) => fixture.sport_name).filter(Boolean))).sort(),
    [fixtures],
  ) as string[];
  const competitionOptions = useMemo(
    () =>
      Array.from(
        new Set(fixtures.map((fixture) => getCompetitionLabel(fixture)).filter(Boolean)),
      ).sort(),
    [fixtures],
  );
  const phaseSelectOptions = useMemo<SelectFieldOption[]>(
    () => [
      { label: "All statuses", value: "all" },
      { label: "Live", value: "live" },
      { label: "Upcoming", value: "upcoming" },
      { label: "Completed", value: "completed" },
    ],
    [],
  );
  const sportSelectOptions = useMemo<SelectFieldOption[]>(
    () => [
      { label: "All sports", value: "all" },
      ...sportOptions.map((sportName) => ({ label: sportName, value: sportName })),
    ],
    [sportOptions],
  );
  const competitionSelectOptions = useMemo<SelectFieldOption[]>(
    () => [
      { label: "All competitions", value: "all" },
      ...competitionOptions.map((competitionName) => ({
        label: competitionName,
        value: competitionName,
      })),
    ],
    [competitionOptions],
  );
  const scopedFixtures = useMemo(() => {
    return fixtures.filter((fixture) => {
      if (sportFilter !== "all" && fixture.sport_name !== sportFilter) {
        return false;
      }
      if (competitionFilter !== "all" && getCompetitionLabel(fixture) !== competitionFilter) {
        return false;
      }
      if (!fixtureMatchesSearch(fixture, searchQuery)) {
        return false;
      }
      return true;
    });
  }, [competitionFilter, fixtures, searchQuery, sportFilter]);
  const phaseCounts = useMemo(
    () =>
      scopedFixtures.reduce(
        (counts, fixture) => {
          counts[getFixturePhase(fixture)] += 1;
          return counts;
        },
        {
          completed: 0,
          live: 0,
          upcoming: 0,
        },
      ),
    [scopedFixtures],
  );
  const filteredFixtures = useMemo(() => {
    if (phaseFilter === "all") {
      return scopedFixtures;
    }
    return scopedFixtures.filter((fixture) => getFixturePhase(fixture) === phaseFilter);
  }, [phaseFilter, scopedFixtures]);
  const groupedFixtures = useMemo(
    () => groupFixturesForShowcase(filteredFixtures),
    [filteredFixtures],
  );
  const hasActiveFilters =
    phaseFilter !== "all" ||
    sportFilter !== "all" ||
    competitionFilter !== "all" ||
    Boolean(searchQuery.trim());
  if (isLoading) {
    return <FixturesShowcaseSkeleton title={title} />;
  }
  if (!fixtures.length) {
    return <EmptyState description={emptyDescription} title={emptyTitle} />;
  }
  return (
    <ShowcaseStack>
      <ShowcaseToolbar>
        <ShowcaseToolbarMeta>
          <ShowcaseToolbarTitle>{title}</ShowcaseToolbarTitle>

          <ShowcaseToolbarSummary>
            <ToolbarBadge>{formatFixtureCountLabel(filteredFixtures.length)} shown</ToolbarBadge>
            <ToolbarBadge>
              <ToolbarBadgeDot />
              {phaseCounts.live} live
            </ToolbarBadge>
            <ToolbarBadge>{sportOptions.length || 1} sports</ToolbarBadge>
          </ShowcaseToolbarSummary>
        </ShowcaseToolbarMeta>

        <ShowcaseToolbarControls>
          <ShowcaseSearchInput
            aria-label="Search fixtures"
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search teams, schools, venue, or status"
            value={searchQuery}
          />

          <ShowcaseFilterDropdown
            aria-label="Filter by status"
            inputId="fixture-filter-status"
            onChange={(value) => setPhaseFilter(value as FixturePhase)}
            options={phaseSelectOptions}
            value={phaseFilter}
          />

          <ShowcaseFilterDropdown
            aria-label="Filter by sport"
            inputId="fixture-filter-sport"
            onChange={setSportFilter}
            options={sportSelectOptions}
            value={sportFilter}
          />

          <ShowcaseFilterDropdown
            aria-label="Filter by competition"
            inputId="fixture-filter-competition"
            onChange={setCompetitionFilter}
            options={competitionSelectOptions}
            value={competitionFilter}
          />

          <ShowcaseClearSlot>
            {hasActiveFilters ? (
              <FixtureActionButton
                $variant="secondary"
                onClick={() => {
                  setSearchQuery("");
                  setPhaseFilter("all");
                  setSportFilter("all");
                  setCompetitionFilter("all");
                }}
                type="button"
              >
                Clear
              </FixtureActionButton>
            ) : null}
          </ShowcaseClearSlot>
        </ShowcaseToolbarControls>
      </ShowcaseToolbar>

      {!filteredFixtures.length ? (
        <FixtureDaySection>
          <EmptyState
            description="Try a broader search, a different sport, or reset the status filter."
            title="No fixtures match these filters"
          />
        </FixtureDaySection>
      ) : (
        <FixtureResults>
          <FixtureCardList>
            {groupedFixtures.map((day) => {
              const dayFixtures = flattenDayFixtures(day);
              if (dayFixtures.length === 1) {
                const fixture = dayFixtures[0];
                return (
                  <FixtureCard
                    editLabel={editLabel}
                    fixture={fixture}
                    key={String(
                      fixture.id ||
                        `${fixture.scheduled_at}-${fixture.competition_name || fixture.sport_name || "fixture"}`,
                    )}
                    onEditFixture={onEditFixture}
                    renderFixtureActions={renderFixtureActions}
                  />
                );
              }

              return (
                <FixtureDayCluster key={day.dateKey}>
                  <DayHeader>
                    <DayMeta>
                      <DayTitle>{day.dateLabel}</DayTitle>
                    </DayMeta>
                    <FixtureCountBadge>
                      {formatFixtureCountLabel(dayFixtures.length)}
                    </FixtureCountBadge>
                  </DayHeader>

                  <FixtureCardList>
                    {dayFixtures.map((fixture) => (
                      <FixtureCard
                        editLabel={editLabel}
                        fixture={fixture}
                        key={String(
                          fixture.id ||
                            `${fixture.scheduled_at}-${fixture.competition_name || fixture.sport_name || "fixture"}`,
                        )}
                        onEditFixture={onEditFixture}
                        renderFixtureActions={renderFixtureActions}
                      />
                    ))}
                  </FixtureCardList>
                </FixtureDayCluster>
              );
            })}
          </FixtureCardList>
        </FixtureResults>
      )}
    </ShowcaseStack>
  );
};
